import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { baseUrl, getConfigWithToken } from '../config/config';
import { toast } from 'react-toastify';
import { IoIosArrowBack, IoIosSearch } from 'react-icons/io';
import { FaPlus, FaMinus } from 'react-icons/fa';

const ViewStateInsurances = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const user = useSelector((state) => state?.user);
    const token = user?.token || localStorage.getItem("token") || localStorage.getItem("user_id");

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [availableInsurances, setAvailableInsurances] = useState([]);
    const [assignedInsurances, setAssignedInsurances] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [stateName, setStateName] = useState(location.state?.stateName || 'State');

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch all available insurances (Public)
            const allRes = await axios.get(`${baseUrl}insuranceProvider/all`);
            const all = (allRes.data && allRes.data.data) || (Array.isArray(allRes.data) && allRes.data) || [];

            // Fetch assigned insurances for this state (Public)
            const assignedRes = await axios.get(`${baseUrl}states/${id}/insurances`);
            const assigned = assignedRes.data && assignedRes.data.data ? assignedRes.data.data : [];

            setAssignedInsurances(assigned);

            // Filter out assigned from available
            const assignedIds = new Set(assigned.map(i => i._id));
            setAvailableInsurances(all.filter(i => !assignedIds.has(i._id)));

            // If stateName is missing, fetch state details could be added here
        } catch (error) {
            console.error("Error fetching state insurance data:", error);
            toast.error("Failed to load insurance lists");
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        setLoading(true);
        try {
            const config = getConfigWithToken(token);
            const insuranceIds = assignedInsurances.map(i => i._id);
            const res = await axios.post(`${baseUrl}states/${id}/insurances/sync`, { insuranceIds }, config);

            if (res.data && res.data.success) {
                toast.success("State insurances updated successfully");
                navigate('/payer-matrix', { state: { activeTab: 'States' } });
            } else {
                toast.error(res.data?.message || "Failed to update mappings");
            }
        } catch (error) {
            console.error("Error syncing state insurances:", error);
            toast.error("An error occurred while saving");
        } finally {
            setLoading(false);
        }
    };

    const addToAssigned = (item) => {
        setAvailableInsurances(prev => prev.filter(i => i._id !== item._id));
        setAssignedInsurances(prev => [...prev, item]);
    };

    const removeFromAssigned = (item) => {
        setAssignedInsurances(prev => prev.filter(i => i._id !== item._id));
        setAvailableInsurances(prev => [...prev, item]);
    };

    const filteredAvailable = useMemo(() => {
        return availableInsurances
            .filter(item => {
                const name = (item.insurance_name || "").toLowerCase();
                const payerId = (item.payer_id || "").toLowerCase();
                const s = searchTerm.toLowerCase();
                return name.includes(s) || payerId.includes(s);
            })
            .sort((a, b) => (a.insurance_name || "").localeCompare(b.insurance_name || ""));
    }, [availableInsurances, searchTerm]);

    const sortedAssigned = useMemo(() => {
        return [...assignedInsurances].sort((a, b) => (a.insurance_name || "").localeCompare(b.insurance_name || ""));
    }, [assignedInsurances]);

    // Styles
    const containerStyle = {
        padding: '20px',
        backgroundColor: '#f8f9fa',
        minHeight: '100vh',
        fontFamily: "'Poppins', sans-serif",
    };

    const headerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px 25px',
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        marginBottom: '20px',
    };

    const backButtonStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        color: '#28BDAD',
        fontWeight: '600',
        fontSize: '14px',
        background: 'none',
        border: 'none',
    };

    const backIconContainerStyle = {
        backgroundColor: '#28BDAD',
        color: '#FFFFFF',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '18px',
    };

    const activeButtonStyle = {
        padding: '10px 35px',
        backgroundColor: '#064260',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
    };

    const dualListContainerStyle = {
        display: 'flex',
        gap: '20px',
        width: '100%',
    };

    const listCardStyle = {
        flex: 1,
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '25px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        minHeight: '500px',
    };

    const titleStyle = {
        fontSize: '20px',
        fontWeight: '700',
        color: '#000000',
        marginBottom: '25px',
        marginTop: 0,
    };

    const searchBarContainer = {
        position: 'relative',
        marginBottom: '20px',
    };

    const searchInputStyle = {
        width: '100%',
        padding: '12px 15px 12px 40px',
        borderRadius: '10px',
        border: 'none',
        backgroundColor: '#F7F8F9',
        fontSize: '14px',
        outline: 'none',
    };

    const insuranceItemStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 15px',
        backgroundColor: '#E9EEF1',
        borderRadius: '8px',
        marginBottom: '10px',
        color: '#1E4A5F',
        fontSize: '14px',
        fontWeight: '500',
    };

    const iconButtonStyle = (type) => ({
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: '#064260',
        color: '#FFFFFF',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        border: 'none',
        fontSize: '12px',
    });

    if (loading && availableInsurances.length === 0) {
        return <div style={{ padding: '50px', textAlign: 'center' }}>Loading...</div>;
    }

    return (
        <div style={containerStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <button style={backButtonStyle} onClick={() => navigate(-1)}>
                    <div style={backIconContainerStyle}>
                        <IoIosArrowBack />
                    </div>
                    <span>Back</span>
                </button>
                {isEditing ? (
                    <button style={activeButtonStyle} onClick={handleSync}>Save</button>
                ) : (
                    <button style={activeButtonStyle} onClick={() => setIsEditing(true)}>Edit</button>
                )}
            </div>

            <div style={dualListContainerStyle}>
                {/* Left List - Available (Only in Edit Mode or hidden if desired, but image shows it in edit) */}
                {isEditing && (
                    <div style={listCardStyle}>
                        <h2 style={titleStyle}>Insurance List</h2>
                        <div style={searchBarContainer}>
                            <IoIosSearch style={{ position: 'absolute', left: '15px', top: '14px', color: '#999', fontSize: '18px' }} />
                            <input
                                type="text"
                                placeholder="Search"
                                style={searchInputStyle}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                            {filteredAvailable.map(item => (
                                <div key={item._id} style={insuranceItemStyle}>
                                    <span>{item.insurance_name} ({item.payer_id || 'N/A'})</span>
                                    <button style={iconButtonStyle()} onClick={() => addToAssigned(item)}>
                                        <FaPlus />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Right List - Assigned */}
                <div style={listCardStyle}>
                    <h2 style={titleStyle}>{stateName} Insurances</h2>
                    <div style={{ maxHeight: '700px', overflowY: 'auto' }}>
                        {sortedAssigned.map(item => (
                            <div key={item._id} style={insuranceItemStyle}>
                                <span>{item.insurance_name} ({item.payer_id || 'N/A'})</span>
                                {isEditing && (
                                    <button style={iconButtonStyle()} onClick={() => removeFromAssigned(item)}>
                                        <FaMinus />
                                    </button>
                                )}
                            </div>
                        ))}
                        {sortedAssigned.length === 0 && !loading && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No insurances assigned</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewStateInsurances;
